// frontend/src/components/LegislativeAssemblyModule.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // <-- Use your custom api instance
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ExpandLess,
  ExpandMore,
  Visibility,
} from '@mui/icons-material';


const LegislativeAssemblyModule = () => {
  const [assemblies, setAssemblies] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedAssembly, setSelectedAssembly] = useState(null);
  const [expandedAssemblyIds, setExpandedAssemblyIds] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [error, setError] = useState('');

  // Get user role from localStorage
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  // Form state holds assembly name and its municipalities/panchayaths (as objects with a "name" property)
  const [formData, setFormData] = useState({
    name: '',
    municipalities: []
  });

  // Fetch all legislative assemblies on component mount
  useEffect(() => {
    fetchAssemblies();
  }, []);

  const fetchAssemblies = async () => {
    try {
      const response = await api.get('/assemblies');
      setAssemblies(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching assemblies:', error);
      if (error.response?.status === 403) {
        setError('Access denied: You do not have permission to view assemblies.');
      } else {
        setError('Failed to fetch assemblies. Please try again.');
      }
    }
  };

  // Handle changes in form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Open the form dialog in add or edit mode
  const handleOpenForm = (assembly = null) => {
    if (assembly) {
      setEditing(true);
      setSelectedAssembly(assembly);
      setFormData({
        name: assembly.name,
        municipalities: assembly.municipalities || []
      });
    } else {
      setEditing(false);
      setSelectedAssembly(null);
      setFormData({
        name: '',
        municipalities: []
      });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({ name: '', municipalities: [] });
    setSelectedAssembly(null);
    setEditing(false);
  };

  // Add a new municipality/panchayath via prompt
  const handleAddMunicipality = () => {
    const municipalityName = prompt('Enter Municipality/Panchayath Name:');
    if (municipalityName) {
      setFormData((prev) => ({
        ...prev,
        municipalities: [...prev.municipalities, { name: municipalityName }]
      }));
    }
  };

  // Remove a municipality from the form list
  const handleDeleteMunicipality = (index) => {
    setFormData((prev) => {
      const updated = [...prev.municipalities];
      updated.splice(index, 1);
      return { ...prev, municipalities: updated };
    });
  };

  // Submit the form data to create or update an assembly
  const handleSubmit = async () => {
    try {
      if (editing && selectedAssembly) {
        // Update the assembly
        await api.put(`/assemblies/${selectedAssembly._id}`, formData);
      } else {
        // Create a new assembly
        await api.post('/assemblies', formData);
      }
      fetchAssemblies();
      handleCloseForm();
      setError('');
    } catch (error) {
      console.error('Error saving assembly:', error);
      if (error.response?.status === 403) {
        setError('Access denied: You do not have permission to perform this action.');
      } else {
        setError('Failed to save assembly. Please try again.');
      }
    }
  };

  // Delete a legislative assembly
  const handleDeleteAssembly = async (assemblyId) => {
    if (window.confirm('Are you sure you want to delete this assembly?')) {
      try {
        await api.delete(`/assemblies/${assemblyId}`);
        fetchAssemblies();
        setError('');
      } catch (error) {
        console.error('Error deleting assembly:', error);
        if (error.response?.status === 403) {
          setError('Access denied: You do not have permission to delete assemblies.');
        } else {
          setError('Failed to delete assembly. Please try again.');
        }
      }
    }
  };

  // Toggle the expanded view for the municipalities list
  const handleToggleExpand = (assemblyId) => {
    if (expandedAssemblyIds.includes(assemblyId)) {
      setExpandedAssemblyIds(expandedAssemblyIds.filter((id) => id !== assemblyId));
    } else {
      setExpandedAssemblyIds([...expandedAssemblyIds, assemblyId]);
    }
  };

  const isAdmin = userRole === 'admin';

  return (
    <Container sx={{ mt: 2 }}>
              <Typography variant="h5" align="center" gutterBottom>
        {isAdmin ? 'Legislative Assembly Management' : 'View Legislative Assemblies'}
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Add Button - Admin Only */}
      {isAdmin && (
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenForm()}>
          Add Legislative Assembly
        </Button>
      )}

      <Paper sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Assembly Name</TableCell>
              <TableCell>Municipalities/Panchayaths</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {assemblies.map((assembly) => (
              <React.Fragment key={assembly._id}>
                <TableRow>
                  <TableCell>{assembly.name}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleToggleExpand(assembly._id)}>
                      {expandedAssemblyIds.includes(assembly._id) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenForm(assembly)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteAssembly(assembly._id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isAdmin ? 3 : 2}>
                    <Collapse
                      in={expandedAssemblyIds.includes(assembly._id)}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box margin={1}>
                        <Typography variant="subtitle1" gutterBottom>
                          Municipalities/Panchayaths:
                        </Typography>
                        {assembly.municipalities && assembly.municipalities.length > 0 ? (
                          <List>
                            {assembly.municipalities.map((muni, index) => (
                              <ListItem key={index}>
                                <ListItemText primary={muni.name} />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2">No municipalities added.</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog for adding/editing an assembly - Admin Only */}
      {isAdmin && (
        <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
          <DialogTitle>{editing ? 'Edit Legislative Assembly' : 'Add Legislative Assembly'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Assembly Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Municipalities/Panchayaths</Typography>
              <Button variant="outlined" size="small" onClick={handleAddMunicipality} startIcon={<Add />}>
                Add Municipality/Panchayath
              </Button>
              {formData.municipalities && formData.municipalities.length > 0 && (
                <List>
                  {formData.municipalities.map((muni, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => handleDeleteMunicipality(index)}>
                          <Delete />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={muni.name} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editing ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default LegislativeAssemblyModule;
